/**
 * The property sidebar's Address tab — single-card layout.
 *
 * One card, top to bottom:
 *   1. The map, full-bleed (Map / Aerial / Street, expand, open).
 *   2. The official address — `PropertyOfficialAddress` (geocode chip, Copy,
 *      Retry verification, and the two-step Change address flow). While it is
 *      open, the sections below are dimmed.
 *   3. Address on title (`stringAddressFinalTitle`) — empty → a dashed "add"
 *      row + "Use official address"; filled → the value with a Matches /
 *      Differs-from-official chip and Edit; editing → input + Save / Cancel.
 *   4. Address details — the geocoded parts, read-only (Modify changes them),
 *      with coordinates / APN / display address behind a footer toggle.
 *
 * Alternate to `PropertyAddressTab`; the sidebar picks with
 * `ADDRESS_TAB_LAYOUT`.
 */
import {
  AddRounded,
  ContentCopyOutlined,
  EditOutlined,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  LockOutlined,
} from "@mui/icons-material";
import { Box, Button, ButtonBase, CircularProgress, TextField } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import { amber, ink, slate, status, teal } from "../../../theme/colors";
import PropertyMap from "../../common/PropertyMap";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import PropertyOfficialAddress, {
  geocodeState,
  normalizeAddress as normalize,
  splitStreet,
} from "./PropertyOfficialAddress";

const LINE = slate[200];
const MAX_W = 1080;

const neutralBtnSx = {
  flexShrink: 0,
  bgcolor: "#fff",
  color: slate[700],
  borderColor: slate[300],
  fontWeight: 500,
  "&:hover": { bgcolor: slate[50], borderColor: slate[400] },
};

const SectionHead = ({ title, note, icon }) => (
  <Flx ac wrap sx={{ gap: "4px 10px" }}>
    <Txt component="span" sx={{ fontSize: 14, fontWeight: 600, color: ink }}>
      {title}
    </Txt>
    <Flx ac sx={{ gap: "4px", fontSize: 12.5, color: slate[500] }}>
      {icon}
      {note}
    </Flx>
  </Flx>
);
SectionHead.propTypes = { title: PropTypes.node, note: PropTypes.node, icon: PropTypes.node };

const Pill = ({ tone, children }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 500,
      px: "8px",
      py: "1px",
      borderRadius: "999px",
      background: tone.bg,
      color: tone.fg,
    }}
  >
    {children}
  </Box>
);
Pill.propTypes = { tone: PropTypes.object, children: PropTypes.node };

/* ── Address on title ─────────────────────────────────── */

const TitleAddressSection = ({ property, loanId, officialAddress }) => {
  const dispatch = useDispatch();
  const { updateSubjectProperty } = useUnderwritingHook();
  const saved = property?.stringAddressFinalTitle || "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Another property, or a save landing, resets the row.
  useEffect(() => {
    setEditing(false);
    setValue(saved);
  }, [property?._id, saved]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEdit = (seed) => {
    setValue(seed);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue(saved);
  };

  const save = useCallback(() => {
    const next = value.trim();
    if (next === saved.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    updateSubjectProperty({
      loanId,
      propertyId: property?._id,
      data: { stringAddressFinalTitle: next || null },
      onSuccessFn: (response) => {
        dispatch(loanDrilldownSet(response));
        dispatch(loanPipelineClear(response));
        setEditing(false);
      },
      onCompleteFn: () => setSaving(false),
    });
  }, [value, saved, updateSubjectProperty, loanId, property?._id, dispatch]);

  const matches = saved && officialAddress && normalize(saved) === normalize(officialAddress);

  return (
    <Flx column sx={{ gap: "12px", px: 2.5, py: 2, borderTop: `1px solid ${LINE}` }}>
      <SectionHead title="Address on title" note="Used on closing documents" />

      {editing ? (
        <Flx ac sx={{ gap: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            value={value}
            disabled={saving}
            placeholder="Address exactly as it appears on title"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            sx={{ "& .MuiInputBase-root": { height: 44, fontSize: 14 } }}
            inputProps={{ "aria-label": "Address on title" }}
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{ height: 44, flexShrink: 0 }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save"}
          </Button>
          <Button
            variant="outlined"
            onClick={cancel}
            disabled={saving}
            sx={{ ...neutralBtnSx, height: 44 }}
          >
            Cancel
          </Button>
        </Flx>
      ) : saved ? (
        <Flx
          ac
          sx={{
            gap: "10px",
            minHeight: 44,
            px: 1.75,
            border: `1px solid ${LINE}`,
            borderRadius: "8px",
            background: "#fff",
          }}
        >
          <Txt component="span" sx={{ fontSize: 14, fontWeight: 500, color: ink, wordBreak: "break-word" }}>
            {saved}
          </Txt>
          {officialAddress ? (
            matches ? (
              <Pill tone={status.success}>Matches official</Pill>
            ) : (
              <Pill tone={{ bg: amber.light, fg: amber.chipText }}>Differs from official</Pill>
            )
          ) : null}
          <Button
            size="small"
            variant="text"
            startIcon={<EditOutlined sx={{ fontSize: "15px !important" }} />}
            onClick={() => startEdit(saved)}
            sx={{ ml: "auto", flexShrink: 0 }}
          >
            Edit
          </Button>
        </Flx>
      ) : (
        <Flx ac sx={{ gap: 1 }}>
          <ButtonBase
            onClick={() => startEdit("")}
            sx={{
              flex: 1,
              justifyContent: "flex-start",
              gap: 1,
              height: 44,
              px: 1.75,
              border: `1px dashed ${slate[300]}`,
              borderRadius: "8px",
              color: slate[500],
              fontSize: 14,
              "&:hover": { borderColor: teal[300], background: teal[50], color: teal[700] },
            }}
          >
            <AddRounded sx={{ fontSize: 18 }} />
            Add the address exactly as it appears on title
          </ButtonBase>
          {officialAddress ? (
            <Button
              variant="outlined"
              startIcon={<ContentCopyOutlined sx={{ fontSize: "15px !important" }} />}
              onClick={() => startEdit(officialAddress)}
              sx={{ ...neutralBtnSx, height: 44 }}
            >
              Use official address
            </Button>
          ) : null}
        </Flx>
      )}
    </Flx>
  );
};
TitleAddressSection.propTypes = {
  property: PropTypes.object,
  loanId: PropTypes.string,
  officialAddress: PropTypes.string,
};

/* ── Address details ──────────────────────────────────── */

const Detail = ({ label, value }) => {
  const empty = value === null || value === undefined || value === "";
  return (
    <Flx column sx={{ gap: "4px", minWidth: 0 }}>
      <Txt component="span" sx={{ fontSize: 12, color: slate[500] }}>
        {label}
      </Txt>
      <Txt
        component="span"
        sx={{ fontSize: 14, fontWeight: 500, color: empty ? slate[400] : ink, wordBreak: "break-word" }}
      >
        {empty ? "—" : String(value)}
      </Txt>
    </Flx>
  );
};
Detail.propTypes = { label: PropTypes.string, value: PropTypes.any };

const DETAIL_GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px 24px",
  "@container (max-width: 560px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
};

const AddressDetailsSection = ({ address: a }) => {
  const [showMore, setShowMore] = useState(false);
  const apnMissing = !a?.apn;
  const street = splitStreet(a);

  return (
    <Box sx={{ borderTop: `1px solid ${LINE}`, containerType: "inline-size" }}>
      <Flx column sx={{ gap: "14px", px: 2.5, py: 2 }}>
        <SectionHead
          title="Address details"
          icon={<LockOutlined sx={{ fontSize: 13 }} />}
          note={
            {
              verified: "Filled from the verified address",
              partial: "As entered — the geocoder only found a nearby address",
            }[geocodeState(a)] || "As entered — not verified. Use Change address to fix."
          }
        />
        <Box sx={DETAIL_GRID_SX}>
          <Detail label="Street number" value={street.streetNumber} />
          <Detail label="Street name" value={street.streetName} />
          <Detail label="Unit" value={a?.addressUnitNumber || a?.unit} />
          <Detail label="City" value={a?.city} />
          <Detail label="State" value={a?.state} />
          <Detail label="ZIP" value={a?.zip} />
          <Detail label="County" value={a?.county} />
          <Detail label="Country" value={a?.country} />
          {showMore ? (
            <>
              <Detail label="Latitude" value={a?.latitude} />
              <Detail label="Longitude" value={a?.longitude} />
              <Detail label="APN" value={a?.apn} />
              <Detail label="Display address" value={a?.displayAddress} />
            </>
          ) : null}
        </Box>
      </Flx>
      <ButtonBase
        onClick={() => setShowMore((v) => !v)}
        sx={{
          width: "100%",
          gap: "6px",
          py: 1.25,
          borderTop: `1px solid ${LINE}`,
          fontSize: 12.5,
          color: slate[500],
          "&:hover": { background: slate[50], color: slate[700] },
        }}
      >
        {showMore
          ? "Hide coordinates and parcel"
          : `Show coordinates and parcel${apnMissing ? " · APN missing" : ""}`}
        {showMore ? (
          <KeyboardArrowUpRounded sx={{ fontSize: 16 }} />
        ) : (
          <KeyboardArrowDownRounded sx={{ fontSize: 16 }} />
        )}
      </ButtonBase>
    </Box>
  );
};
AddressDetailsSection.propTypes = { address: PropTypes.object };

/* ── the tab ──────────────────────────────────────────── */

const PropertyAddressCardTab = ({ property, loanId }) => {
  const official = addressLabel(property?.address, "");
  // While the official address is being changed, the sections below it are
  // dimmed and inert — the change flow owns the card until it saves or cancels.
  const [changing, setChanging] = useState(false);
  return (
    <Box sx={{ px: 2, py: 3 }}>
      <Box
        sx={{
          maxWidth: MAX_W,
          mx: "auto",
          border: `1px solid ${LINE}`,
          borderRadius: "10px",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <PropertyMap
          address={property?.address}
          property={property}
          loanId={loanId}
          height="240px"
          expandedHeight="560px"
          expandable
          sx={{ border: "none", borderRadius: 0 }}
        />
        <Box sx={{ borderTop: `1px solid ${LINE}` }}>
          <PropertyOfficialAddress property={property} loanId={loanId} onEditingChange={setChanging} />
        </Box>
        <Box
          aria-disabled={changing || undefined}
          sx={{
            opacity: changing ? 0.45 : 1,
            pointerEvents: changing ? "none" : "auto",
            transition: "opacity .15s",
          }}
        >
          <TitleAddressSection property={property} loanId={loanId} officialAddress={official} />
          <AddressDetailsSection address={property?.address} />
        </Box>
      </Box>
    </Box>
  );
};

PropertyAddressCardTab.propTypes = {
  property: PropTypes.object,
  loanId: PropTypes.string,
};

export default PropertyAddressCardTab;
